import { useCallback, useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import CreateEditShipmentData from "./CreateEditShipmentData";
import { useAppDispatch, useSelector } from "../../../redux-store";
import { addLine } from "../../../ducks/data/deliveries";
import { computeDraftLines } from "../../../ducks/data/draft-invoice-lines";
import {
  selectPricingAgreementsForClient,
  selectProducts,
} from "../../../ducks/data/products/selectors";
import {
  selectInvoiceLines,
  selectInvoiceLinesForCreate,
} from "../../../ducks/data/deliveries/selectors";
import { notifyL } from "../../../ducks/notifications";
import { watchFormChanges } from "./watch-form-changes";

const useCreateInvoiceLinesOnSelectedProducts = (
  formMethods: UseFormReturn<CreateEditShipmentData>,
  initialPrevProductsIds: string[],
  deliveryId: string | null,
  // When creating a cargo (no delivery yet), lines are computed in memory
  // through the same surcharge logic as on a persisted delivery.
  createNew?: boolean
) => {
  const dispatch = useAppDispatch();
  const products = useSelector(selectProducts);

  const { getValues, watch } = formMethods;

  const clientId = watch("clientId");
  const pricingAgreements = useSelector(
    selectPricingAgreementsForClient(clientId || "")
  );

  // Products that already have an invoice line (e.g. lines loaded from a
  // selected template). Used to avoid creating a duplicate line for them.
  const existingLines = useSelector(
    createNew
      ? selectInvoiceLinesForCreate()
      : selectInvoiceLines(deliveryId ?? "")
  );

  const [prevProductsIds, setPrevProductsIds] = useState<string[]>(
    initialPrevProductsIds
  );

  const addLineForProduct = useCallback(
    (productId: string) => {
      const pricingAgreementForProduct = pricingAgreements.find(
        (pa) => pa.productId === productId
      );
      const actualProduct = products.find((p) => p.id === productId);
      if (!actualProduct) return;
      const line = {
        productId,
        amount:
          pricingAgreementForProduct?.salesPrice ||
          actualProduct.salesPrice ||
          "1",
        quantity: pricingAgreementForProduct?.unitNumber?.toString() || "1",
        unitId:
          pricingAgreementForProduct?.unitId || actualProduct.unitId || null,
        description: pricingAgreementForProduct?.name || actualProduct.name,
        discountPercentage:
          pricingAgreementForProduct?.discountPercentage || null,
      };
      const created = createNew
        ? dispatch(
            computeDraftLines({
              clientId: clientId || null,
              op: { type: "ADD", line },
            })
          ).unwrap()
        : dispatch(addLine({ deliveryId: deliveryId!, ...line })).unwrap();
      created
        .then(() => {
          dispatch(
            notifyL({
              namespace: "notifications",
              type: "success",
              key: "invoiceLineCreated",
            })
          );
        })
        .catch(() => {
          // The computeDraftLines / addLine thunk has already reported it and shown the error toast.
        });
    },
    [pricingAgreements, products, createNew, dispatch, clientId, deliveryId]
  );

  // Products selected through the input (e.g. keyboard) get one line each,
  // unless they already have one. Programmatic changes (setValue) only sync
  // the previous ids - callers adding lines explicitly use addLineForProduct.
  const reactOnProductsChange = useCallback(
    (changes: any) => {
      if (changes.name !== "products") return;
      const currentProducts = getValues("products").filter(Boolean);
      if (changes.type === "change") {
        const newProducts = currentProducts.filter(
          (p) =>
            !prevProductsIds.find((pId) => pId === p.id) &&
            !existingLines.some((l) => l.productId === p.id)
        );
        for (const product of newProducts) {
          addLineForProduct(product.id);
        }
      }
      setPrevProductsIds(currentProducts.map((p) => p.id));
    },
    [getValues, prevProductsIds, existingLines, addLineForProduct]
  );

  const run = useCallback(
    (changes: any) => {
      reactOnProductsChange(changes);
    },
    [reactOnProductsChange]
  );

  const [hasNotRunTheFirstTime, setHasNotRunTheFirstTime] = useState(true);

  useEffect(() => {
    if (hasNotRunTheFirstTime) {
      setHasNotRunTheFirstTime(false);
      return;
    }

    const subscription = watchFormChanges(watch, run);
    return () => subscription.unsubscribe();
  }, [watch, run, hasNotRunTheFirstTime, setHasNotRunTheFirstTime]);

  const lineCountByProductId = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const line of existingLines) {
      if (line.productId) {
        counts[line.productId] = (counts[line.productId] || 0) + 1;
      }
    }
    return counts;
  }, [existingLines]);

  return { addLineForProduct, lineCountByProductId };
};

export default useCreateInvoiceLinesOnSelectedProducts;
